import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Button, IconButton, TextField, Chip, Stack,
  CircularProgress, Alert, Divider, Menu, MenuItem, Switch, FormControlLabel,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReactMarkdown from 'react-markdown';
import {
  listCaseNotes,
  createCaseNote,
  updateCaseNote,
  deleteCaseNote
} from '../../services/caseNotesApi';

function formatWhen(d) {
  if (!d) return '';
  try { return new Date(d).toLocaleString(); } catch { return ''; }
}

function TagInput({ tags, onChange }) {
  const [value, setValue] = useState('');
  const add = () => {
    const t = value.trim();
    if (!t) return;
    if (tags.includes(t)) { setValue(''); return; }
    onChange([...tags, t]);
    setValue('');
  };
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5, alignItems: 'center' }}>
      {tags.map(t => (
        <Chip key={t} label={t} size="small" onDelete={() => onChange(tags.filter(x => x !== t))} />
      ))}
      <TextField
        size="small"
        variant="standard"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(); }
        }}
        placeholder="add tag"
        sx={{ minWidth: 100 }}
      />
    </Stack>
  );
}

function NoteEditor({ initial, onCancel, onSave, saving }) {
  const [title, setTitle] = useState(initial?.title || '');
  const [body, setBody] = useState(initial?.body || '');
  const [tags, setTags] = useState(initial?.tags || []);
  const [pinned, setPinned] = useState(!!initial?.pinned);
  const [visibility, setVisibility] = useState(initial?.visibility || 'private');

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <TextField
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Title (optional)"
        fullWidth
        size="small"
        sx={{ mb: 1.5 }}
      />
      <TextField
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Your working notes, formulation, reflections… (Markdown supported)"
        multiline
        minRows={5}
        maxRows={16}
        fullWidth
        size="small"
        inputProps={{ maxLength: 20000 }}
      />
      <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <TagInput tags={tags} onChange={setTags} />
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControlLabel
            control={<Switch size="small" checked={pinned} onChange={(e) => setPinned(e.target.checked)} />}
            label="Pin"
          />
          <Tooltip title={visibility === 'supervisor_readable'
            ? 'Your clinical supervisor can read this note.'
            : 'Private — only you can see this note.'}>
            <FormControlLabel
              control={<Switch size="small" checked={visibility === 'supervisor_readable'} onChange={(e) => setVisibility(e.target.checked ? 'supervisor_readable' : 'private')} />}
              label="Supervisor can read"
            />
          </Tooltip>
        </Stack>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1.5 }}>
        <Button onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => onSave({ title, body, tags, pinned, visibility })}
          disabled={saving || !body.trim()}
        >
          {saving ? 'Saving…' : (initial?._id ? 'Save changes' : 'Create note')}
        </Button>
      </Box>
    </Paper>
  );
}

function NoteCard({ note, onEdit, onTogglePin, onDelete }) {
  const [anchor, setAnchor] = useState(null);
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 1.5, position: 'relative' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        {note.pinned && <PushPinIcon fontSize="small" color="warning" />}
        {note.title && <Typography variant="subtitle2">{note.title}</Typography>}
        <Box sx={{ flex: 1 }} />
        <Tooltip title={note.visibility === 'supervisor_readable'
          ? 'Supervisor can read'
          : 'Private'}>
          {note.visibility === 'supervisor_readable'
            ? <VisibilityIcon fontSize="small" color="action" />
            : <LockOutlinedIcon fontSize="small" color="action" />}
        </Tooltip>
        <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
          <MenuItem onClick={() => { setAnchor(null); onEdit(note); }}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
          </MenuItem>
          <MenuItem onClick={() => { setAnchor(null); onTogglePin(note); }}>
            {note.pinned
              ? <><PushPinOutlinedIcon fontSize="small" sx={{ mr: 1 }} /> Unpin</>
              : <><PushPinIcon fontSize="small" sx={{ mr: 1 }} /> Pin</>}
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => { setAnchor(null); onDelete(note); }} sx={{ color: 'error.main' }}>
            <DeleteOutlineIcon fontSize="small" sx={{ mr: 1 }} /> Delete
          </MenuItem>
        </Menu>
      </Box>
      <Box sx={{
        fontSize: 14,
        '& p':  { m: 0, mb: 0.5 },
        '& h1, & h2, & h3': { fontSize: 15, m: 0, mb: 0.5 },
        '& ul, & ol': { m: 0, pl: 3 }
      }}>
        <ReactMarkdown>{note.body}</ReactMarkdown>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
        {(note.tags || []).map(t => <Chip key={t} label={t} size="small" variant="outlined" />)}
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">
          Updated {formatWhen(note.updatedAt)}
        </Typography>
      </Box>
    </Paper>
  );
}

/**
 * Therapist's private working notes on a client. Distinct from Care Notes
 * (AI-generated guidance) and message annotations (per-message).
 */
export default function CaseNotesTab({ token, clientId }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notes, setNotes] = useState([]);
  const [tagFilter, setTagFilter] = useState(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null); // note being edited
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { notes: list } = await listCaseNotes({ token, clientId });
      setNotes(list || []);
    } catch (err) {
      setError(err?.response?.data?.msg || 'Could not load notes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (clientId) load(); /* eslint-disable-next-line */ }, [clientId]);

  const allTags = useMemo(() => {
    const set = new Set();
    notes.forEach(n => (n.tags || []).forEach(t => set.add(t)));
    return Array.from(set).sort();
  }, [notes]);

  const visible = useMemo(() => {
    if (!tagFilter) return notes;
    return notes.filter(n => (n.tags || []).includes(tagFilter));
  }, [notes, tagFilter]);

  const handleCreate = async (payload) => {
    setSaving(true);
    try {
      await createCaseNote({ token, clientId, ...payload });
      setCreating(false);
      await load();
    } catch (err) {
      setError(err?.response?.data?.msg || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (payload) => {
    if (!editing) return;
    setSaving(true);
    try {
      await updateCaseNote({ token, noteId: editing._id, ...payload });
      setEditing(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.msg || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePin = async (note) => {
    try {
      await updateCaseNote({ token, noteId: note._id, pinned: !note.pinned });
      await load();
    } catch (err) {
      setError(err?.response?.data?.msg || 'Update failed');
    }
  };

  const handleDelete = async (note) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await deleteCaseNote({ token, noteId: note._id });
      await load();
    } catch (err) {
      setError(err?.response?.data?.msg || 'Delete failed');
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h6">Case notes</Typography>
          <Typography variant="body2" color="text.secondary">
            Your private working notes on this client. Not visible to the client.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setCreating(true); setEditing(null); }}>
          New note
        </Button>
      </Box>

      {allTags.length > 0 && (
        <Stack direction="row" spacing={0.5} sx={{ mb: 2, flexWrap: 'wrap', gap: 0.5 }}>
          <Chip
            label="All"
            size="small"
            variant={tagFilter === null ? 'filled' : 'outlined'}
            color={tagFilter === null ? 'primary' : 'default'}
            onClick={() => setTagFilter(null)}
          />
          {allTags.map(t => (
            <Chip
              key={t}
              label={t}
              size="small"
              variant={tagFilter === t ? 'filled' : 'outlined'}
              color={tagFilter === t ? 'primary' : 'default'}
              onClick={() => setTagFilter(t)}
            />
          ))}
        </Stack>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {creating && (
        <NoteEditor
          initial={null}
          saving={saving}
          onCancel={() => setCreating(false)}
          onSave={handleCreate}
        />
      )}

      {editing && (
        <NoteEditor
          initial={editing}
          saving={saving}
          onCancel={() => setEditing(null)}
          onSave={handleUpdate}
        />
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : visible.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {tagFilter
              ? `No notes with tag "${tagFilter}".`
              : 'No case notes yet. Click New note to start keeping your working notes on this client.'}
          </Typography>
        </Paper>
      ) : (
        <Box>
          {visible.map(n => (
            <NoteCard
              key={n._id}
              note={n}
              onEdit={(note) => { setEditing(note); setCreating(false); }}
              onTogglePin={handleTogglePin}
              onDelete={handleDelete}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
